import type { BookingListItemDTO } from '@six-pockets/shared';
import { PencilIcon, TrashIcon } from '@/components/icons';
import { Button, StatusDot } from '@/components/ui';
import { bookingDotStatus } from '@/features/tables/status';
import { useNow } from '@/hooks/useNow';
import { elapsedMs, isLive } from '@/utils/booking';
import { cx } from '@/utils/cx';
import { formatClock, formatDuration, formatINR, formatShortDuration } from '@/utils/format';
import styles from '@/styles/components/BookingListRow/style.scss';

interface BookingListRowProps {
  booking: BookingListItemDTO;
  /** Unbilled bookings only. */
  onEdit?: () => void;
  /** Unbilled bookings, admin only. */
  onDelete?: () => void;
}

/** Duration cell: the live play timer while in play or paused; once ended, the charged time, start to end. */
function duration(booking: BookingListItemDTO, now: number): string {
  if (isLive(booking.status)) return formatDuration(elapsedMs(booking, now));
  return formatShortDuration(new Date(booking.endedAt!).getTime() - new Date(booking.startedAt).getTime());
}

/**
 * One booking on the Bookings page, laid out like a table card's row:
 * dot · name · table · start · end · duration · amount · edit (and delete, for admin). Two lines when the list is narrow.
 */
export function BookingListRow({ booking, onEdit, onDelete }: BookingListRowProps) {
  const live = isLive(booking.status);
  const now = useNow(booking.status === 'active' ? 1000 : null);
  const status = bookingDotStatus(booking);
  // Doubles: both names (each team gets its own bill).
  const names = [booking.customerName, booking.customerName2].filter(Boolean).join(' | ');
  const who = names || 'unnamed booking';
  const pauseNote = !live && booking.pausedMs > 0 ? `Includes ${formatShortDuration(booking.pausedMs)} paused` : undefined;

  return (
    <li className={cx(styles.row, live && styles.live, booking.billStatus === 'billed' && styles.billed)}>
      <StatusDot tone={status.tone} label={status.label} className={styles.dot} />
      <span className={cx(styles.name, !names && styles.noName)} title={names || undefined}>
        {names || 'No name'}
      </span>
      <span className={styles.table}>{booking.tableName}</span>
      <span className={cx(styles.time, styles.start)}>{formatClock(booking.startedAt)}</span>
      <span className={cx(styles.time, styles.end)}>{booking.endedAt ? formatClock(booking.endedAt) : 'Open'}</span>
      <span
        className={cx(styles.duration, booking.status === 'active' && styles.running, booking.status === 'paused' && styles.paused, !live && styles.ended)}
        title={pauseNote}
      >
        {duration(booking, now)}
      </span>
      <span className={styles.amount}>{booking.amount !== null ? formatINR(booking.amount) : ''}</span>
      <div className={styles.actions}>
        {onEdit && <Button size="sm" variant="ghost" iconOnly icon={<PencilIcon size={15} />} aria-label={`Edit ${who}`} title="Edit" onClick={onEdit} />}
        {onDelete && (
          <Button size="sm" variant="ghost" iconOnly icon={<TrashIcon size={15} />} aria-label={`Delete ${who}`} title="Delete" onClick={onDelete} />
        )}
      </div>
    </li>
  );
}
